/**
 * Google Calendar Service
 * Fetches real-time availability from Google Calendar
 */

// Extract calendar ID from Google Calendar URL
// Format: https://calendar.google.com/calendar/u/1/r/tasks?pli=1
// For public calendar, we can use the calendar ID directly
const CALENDAR_ID = 'primary'; // Change this to your actual calendar ID
const API_KEY = process.env.REACT_APP_GOOGLE_CALENDAR_API_KEY || '';

/**
 * Get calendar ID from Google Calendar URL
 * If you have a public calendar, you can use its public iCal feed
 */
export const getCalendarIdFromUrl = (url) => {
  if (!url) return null;
  
  // If it's already a calendar ID (contains @), return as is
  if (url.includes('@') && !url.startsWith('http')) {
    return url;
  }
  
  // Extract calendar ID from various Google Calendar URL formats
  const patterns = [
    // Format: https://calendar.google.com/calendar/embed?src=CALENDAR_ID
    /calendar\.google\.com\/calendar\/embed\?src=([^&]+)/,
    // Format: https://calendar.google.com/calendar/ical/CALENDAR_ID/public/basic.ics
    /calendar\.google\.com\/calendar\/ical\/([^/]+)/,
    // Format: https://calendar.google.com/calendar/u/0?cid=CALENDAR_ID
    /[?&]cid=([^&]+)/,
    // Format: calendar ID directly (email format)
    /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return decodeURIComponent(match[1]);
    }
  }
  
  // If URL is tasks view, we need the user to provide their calendar ID
  // For now, return 'primary' which is the default calendar
  if (url.includes('tasks') || url.includes('calendar.google.com')) {
    // Try to extract from URL or use primary
    // User should set REACT_APP_GOOGLE_CALENDAR_URL to their actual calendar ID
    return 'primary';
  }
  
  return null;
};

/**
 * Fetch events from Google Calendar using public iCal feed
 * This works without authentication for public calendars
 */
export const fetchCalendarEventsFromICal = async (calendarId) => {
  try {
    // Handle different calendar ID formats
    let icalUrl;
    
    if (calendarId === 'primary' || !calendarId) {
      // For primary calendar, we need the user's email
      // This won't work without proper calendar ID
      console.warn('Calendar ID is "primary". Please provide your actual calendar ID for iCal feed.');
      return [];
    }
    
    // Convert calendar ID to iCal feed URL
    // Format: https://calendar.google.com/calendar/ical/CALENDAR_ID/public/basic.ics
    icalUrl = `https://calendar.google.com/calendar/ical/${encodeURIComponent(calendarId)}/public/basic.ics`;
    
    const response = await fetch(icalUrl);
    if (!response.ok) {
      // If public feed fails, try private feed (may require auth)
      throw new Error(`Failed to fetch calendar: ${response.status}`);
    }
    
    const icalData = await response.text();
    
    // Check if we got valid iCal data
    if (!icalData.includes('BEGIN:VCALENDAR')) {
      throw new Error('Invalid iCal data received');
    }
    
    return parseICalData(icalData);
  } catch (error) {
    console.error('Error fetching iCal feed:', error);
    // Return empty array on error - calendar will show as unavailable
    return [];
  }
};

/**
 * Parse iCal data to extract events
 */
const parseICalData = (icalData) => {
  const events = [];
  const lines = icalData.split('\n');
  let currentEvent = null;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line === 'BEGIN:VEVENT') {
      currentEvent = {};
    } else if (line === 'END:VEVENT' && currentEvent) {
      if (currentEvent.start && currentEvent.end) {
        events.push(currentEvent);
      }
      currentEvent = null;
    } else if (currentEvent) {
      if (line.startsWith('DTSTART')) {
        currentEvent.start = parseICalDate(line);
      } else if (line.startsWith('DTEND')) {
        currentEvent.end = parseICalDate(line);
      } else if (line.startsWith('SUMMARY')) {
        currentEvent.summary = line.substring(8).trim();
      }
    }
  }
  
  return events;
};

/**
 * Parse iCal date format (YYYYMMDDTHHMMSS or YYYYMMDD)
 */
const parseICalDate = (line) => {
  const dateStr = line.split(':')[1]?.replace(/[TZ]/g, '') || '';
  if (dateStr.length >= 8) {
    const year = parseInt(dateStr.substring(0, 4));
    const month = parseInt(dateStr.substring(4, 6)) - 1;
    const day = parseInt(dateStr.substring(6, 8));
    return new Date(year, month, day);
  }
  return null;
};

/**
 * Fetch events from Google Calendar API
 * Requires API key and proper authentication
 */
export const fetchCalendarEventsFromAPI = async (calendarId, timeMin, timeMax) => {
  if (!API_KEY) {
    console.warn('Google Calendar API key not configured');
    return [];
  }

  try {
    const url = new URL('https://www.googleapis.com/calendar/v3/calendars/' + encodeURIComponent(calendarId) + '/events');
    url.searchParams.append('key', API_KEY);
    url.searchParams.append('timeMin', timeMin.toISOString());
    url.searchParams.append('timeMax', timeMax.toISOString());
    url.searchParams.append('singleEvents', 'true');
    url.searchParams.append('orderBy', 'startTime');

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`Calendar API error: ${response.status}`);
    }

    const data = await response.json();
    return data.items || [];
  } catch (error) {
    console.error('Error fetching calendar events:', error);
    return [];
  }
};

/**
 * Get availability for a date range
 * Returns an object with date keys and availability status
 */
export const getAvailabilityForDateRange = async (startDate, endDate, calendarUrl = null) => {
  const availability = {};
  const calendarId = calendarUrl ? getCalendarIdFromUrl(calendarUrl) : CALENDAR_ID;
  
  if (!calendarId) {
    console.warn('Calendar ID not found, using default');
  }

  try {
    // Try API first if API key is available
    let events = [];
    if (API_KEY) {
      events = await fetchCalendarEventsFromAPI(calendarId || 'primary', startDate, endDate);
    } else {
      // Fall back to iCal feed for public calendars
      events = await fetchCalendarEventsFromICal(calendarId || 'primary');
    }

    // Process events to determine availability
    const bookedDates = new Set();
    const eventCounts = {};

    events.forEach(event => {
      const eventStart = event.start?.dateTime 
        ? new Date(event.start.dateTime) 
        : new Date(event.start?.date || event.start);
      
      const eventEnd = event.end?.dateTime 
        ? new Date(event.end.dateTime) 
        : new Date(event.end?.date || event.end);

      // Mark all dates in the event range as having a booking
      const currentDate = new Date(eventStart);
      while (currentDate <= eventEnd) {
        const dateKey = currentDate.toISOString().split('T')[0];
        bookedDates.add(dateKey);
        eventCounts[dateKey] = (eventCounts[dateKey] || 0) + 1;
        currentDate.setDate(currentDate.getDate() + 1);
      }
    });

    // Generate availability for the date range
    const currentDate = new Date(startDate);
    const maxDate = new Date(endDate);
    const maxBookingsPerDay = 4; // Maximum bookings allowed per day

    while (currentDate <= maxDate) {
      const dateKey = currentDate.toISOString().split('T')[0];
      const bookings = eventCounts[dateKey] || 0;
      const isFullyBooked = bookings >= maxBookingsPerDay;
      const isAvailable = !isFullyBooked && bookings < maxBookingsPerDay;

      availability[dateKey] = {
        available: isAvailable,
        fullyBooked: isFullyBooked,
        bookings: bookings,
        maxBookings: maxBookingsPerDay
      };

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return availability;
  } catch (error) {
    console.error('Error getting availability:', error);
    // Return empty availability on error
    return {};
  }
};

/**
 * Check if a specific date is available
 */
export const isDateAvailable = async (date, calendarUrl = null) => {
  const startDate = new Date(date);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(date);
  endDate.setHours(23, 59, 59, 999);

  const availability = await getAvailabilityForDateRange(startDate, endDate, calendarUrl);
  const dateKey = date.toISOString().split('T')[0];
  
  return availability[dateKey]?.available || false;
};

/**
 * Get calendar ID from the provided URL
 * Default calendar URL: https://calendar.google.com/calendar/u/1/r/tasks?pli=1
 */
export const getDefaultCalendarId = () => {
  // For now, return 'primary' - user should replace with their actual calendar ID
  // To get calendar ID: 
  // 1. Go to Google Calendar settings
  // 2. Find your calendar
  // 3. Scroll to "Integrate calendar" section
  // 4. Copy the "Calendar ID" (usually your email or a custom ID)
  return 'primary';
};

